import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { ROLE_LABELS } from "@/lib/api";
import Logo from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";
import {
  LayoutDashboard, FolderKanban, FileText, ClipboardList,
  Users, LogOut, MessageSquare, Wallet, PlusCircle,
  Library, ShieldCheck, Inbox,
} from "lucide-react";

const ALL_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["customer","surveyor","estimator","project_manager","admin"] },
  { to: "/admin/inbox", label: "Inbox", icon: Inbox, roles: ["admin"], badge: "inbox" },
  { to: "/projects", label: "Projects", icon: FolderKanban, roles: ["customer","surveyor","estimator","project_manager","admin"] },
  { to: "/quotes", label: "Quotes", icon: FileText, roles: ["customer","estimator","admin"] },
  { to: "/surveys", label: "Surveys", icon: ClipboardList, roles: ["customer","surveyor","admin"] },
  { to: "/messages", label: "Messages", icon: MessageSquare, roles: ["customer","surveyor","estimator","project_manager","admin"] },
  { to: "/admin", label: "Admin", icon: Users, roles: ["admin"] },
  { to: "/admin/catalog", label: "Catalog", icon: Library, roles: ["admin"] },
  { to: "/admin/audit", label: "Audit log", icon: ShieldCheck, roles: ["admin"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const role = user?.role || "customer";
  const nav = ALL_NAV.filter((n) => n.roles.includes(role));

  // Admin: live inbox count for the red-dot badge.
  const [inboxCount, setInboxCount] = useState(0);
  useEffect(() => {
    if (role !== "admin") return;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/admin/inbox/count");
        if (alive) setInboxCount(data.total || 0);
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 30_000);
    return () => { alive = false; clearInterval(id); };
  }, [role]);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bq-bg)" }}>
      {/* Sidebar */}
      <aside className="w-64 shrink-0 hidden md:flex flex-col border-r bq-dark" data-testid="app-sidebar">
        <div className="px-6 py-5 flex items-center justify-between border-b border-white/10">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="brand-link">
            <Logo variant="word" height={28} linkTo={null} invert testId="brand-logo-sidebar" />
          </Link>
          <NotificationBell />
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-all ${
                  isActive ? "bg-white/10 text-white" : "text-white/70 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <item.icon size={18} />
              <span className="flex-1">{item.label}</span>
              {item.badge === "inbox" && inboxCount > 0 && (
                <span
                  className="min-w-[20px] h-5 px-1.5 rounded-full bg-[var(--bq-orange)] text-white text-[10px] font-bold flex items-center justify-center"
                  data-testid="sidebar-inbox-badge"
                >
                  {inboxCount > 99 ? "99+" : inboxCount}
                </span>
              )}
            </NavLink>
          ))}
          {role === "customer" && (
            <Link
              to="/projects/new"
              className="mt-6 mx-1 flex items-center justify-center gap-2 bq-orange-btn px-3 py-2.5 rounded-md text-sm font-semibold"
              data-testid="sidebar-new-project-btn"
            >
              <PlusCircle size={16} /> New Project
            </Link>
          )}
        </nav>
        <div className="px-4 py-4 border-t border-white/10">
          <div className="text-xs text-white/60 uppercase tracking-widest">{ROLE_LABELS[role]}</div>
          <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="text-xs text-white/50 truncate">{user?.email}</div>
          <button
            onClick={onLogout}
            data-testid="logout-btn"
            className="mt-3 w-full flex items-center justify-center gap-2 text-xs text-white/80 hover:text-white border border-white/15 hover:border-white/30 py-2 rounded-md transition-all"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bq-dark flex items-center justify-between px-4 py-3" data-testid="mobile-topbar">
        <Link to="/dashboard" className="flex items-center gap-2 text-white">
          <Logo variant="word" height={22} linkTo={null} invert testId="brand-logo-mobile" />
        </Link>
        <div className="flex items-center gap-2">
          <NotificationBell />
          <button onClick={onLogout} className="text-white/80 text-xs" data-testid="mobile-logout">
            Logout
          </button>
        </div>
      </div>

      {/* Main */}
      <main className="flex-1 md:pl-0 pt-14 md:pt-0 overflow-y-auto">
        {children}
      </main>
    </div>
  );
}
